import React, { useState } from 'react';
import { X, User, TrendingUp, Award, Activity, Heart, Utensils, Brain, Zap } from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';

export default function BiharAthleteDetailModal({ athlete, onClose, allAthletes = [] }) {
  const [activeTab, setActiveTab] = useState('overview');

  if (!athlete) return null;

  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '-';
    return Number(num).toFixed(1);
  };

  const formatTime = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.round((seconds % 1) * 100);
    return `${mins}:${secs.toString().padStart(2, '0')}:${ms.toString().padStart(2, '0')}`;
  };

  const bmi = athlete.bmi || (athlete.weight_kg && athlete.height_cm 
    ? (athlete.weight_kg / Math.pow(athlete.height_cm / 100, 2)).toFixed(1)
    : null);

  const getBMICategory = (bmi) => {
    if (!bmi) return 'N/A';
    if (bmi < 18.5) return 'Underweight';
    if (bmi < 25) return 'Normal';
    if (bmi < 30) return 'Overweight';
    return 'Obese';
  };

  const bmiCategory = getBMICategory(bmi);

  const radarData = [
    { metric: 'Height', value: athlete.height_percentile || 50, fullMark: 100 },
    { metric: 'Weight', value: athlete.weight_percentile || 50, fullMark: 100 },
    { metric: 'V. Jump', value: athlete.vertical_jump_percentile || 50, fullMark: 100 },
    { metric: 'Broad Jump', value: athlete.broad_jump_percentile || 50, fullMark: 100 },
    { metric: '30m Sprint', value: athlete.sprint_30m_percentile || 50, fullMark: 100 },
    { metric: '800m Run', value: athlete.run_800m_percentile || 50, fullMark: 100 },
  ];

  const getSportSuitability = () => {
    const suits = [];
    const height = athlete.height_cm || 0;
    const weight = athlete.weight_kg || 0;
    const verticalJump = athlete.vertical_jump_cm || 0;
    const broadJump = athlete.broad_jump_cm || 0;
    const sprint = athlete.sprint_30m_sec || 999;
    const endurance = athlete.run_800m_sec || 999;
    const shuttleRun = athlete.shuttle_run_sec || 999;
    const isFemale = (athlete.gender_normalized || athlete.gender || '').toLowerCase() === 'female' || 
                     (athlete.gender_normalized || athlete.gender || '').toLowerCase() === 'f';

    // Gender-aware thresholds
    const t = {
      sprintElite: isFemale ? 6.5 : 6,
      sprintGood: isFemale ? 7.5 : 7,
      bJumpHigh: isFemale ? 160 : 180,
      bJumpMid: isFemale ? 140 : 150,
      bJumpLow: isFemale ? 130 : 160,
      shuttleFast: isFemale ? 17 : 16,
      shuttleMid: isFemale ? 19 : 18,
      vJumpHigh: isFemale ? 30 : 35,
      vJumpMid: isFemale ? 22 : 25,
      vJumpElite: isFemale ? 25 : 30,
      heightTall: isFemale ? 160 : 170,
      heightMid: isFemale ? 155 : 160,
      heightMin: isFemale ? 150 : 160,
      weightMin: isFemale ? 42 : 50,
      enduranceElite: isFemale ? 270 : 240,
      enduranceGood: isFemale ? 330 : 300,
      weightMax: isFemale ? 55 : 65,
      heightCycling: isFemale ? 150 : 155
    };

    // Football - requires speed, agility, and power
    let footballScore = 0;
    const footballStrengths = [];
    const footballWeaknesses = [];
    if (sprint < t.sprintElite) { footballScore += 25; footballStrengths.push('Excellent speed'); }
    else if (sprint < t.sprintGood) { footballScore += 15; footballStrengths.push('Good speed'); }
    else { footballWeaknesses.push('Speed needs improvement'); }
    if (broadJump > t.bJumpHigh) { footballScore += 20; footballStrengths.push('Strong power'); }
    else if (broadJump > t.bJumpMid) { footballScore += 10; }
    else { footballWeaknesses.push('Power development needed'); }
    if (shuttleRun < t.shuttleFast) { footballScore += 20; footballStrengths.push('Great agility'); }
    else if (shuttleRun < t.shuttleMid) { footballScore += 10; }
    if (verticalJump > t.vJumpMid) { footballScore += 15; footballStrengths.push('Good jumping'); }
    footballScore += 20; // Base score
    suits.push({ sport: 'Football', score: Math.min(footballScore, 100), strengths: footballStrengths, weaknesses: footballWeaknesses });

    // Kabaddi - requires strength, agility, and endurance
    let kabaddiScore = 0;
    const kabaddiStrengths = [];
    const kabaddiWeaknesses = [];
    if (weight > t.weightMin && height > t.heightMin) { kabaddiScore += 20; kabaddiStrengths.push('Good build'); }
    if (shuttleRun < t.shuttleFast) { kabaddiScore += 25; kabaddiStrengths.push('Excellent agility'); }
    else if (shuttleRun < t.shuttleMid + 1) { kabaddiScore += 15; kabaddiStrengths.push('Good agility'); }
    else { kabaddiWeaknesses.push('Agility needs work'); }
    if (endurance < t.enduranceGood) { kabaddiScore += 20; kabaddiStrengths.push('Good stamina'); }
    if (broadJump > t.bJumpLow) { kabaddiScore += 15; kabaddiStrengths.push('Good power'); }
    kabaddiScore += 20; // Base score
    suits.push({ sport: 'Kabaddi', score: Math.min(kabaddiScore, 100), strengths: kabaddiStrengths, weaknesses: kabaddiWeaknesses });

    // Athletics (Track & Field) - sprint focus
    let athleticsScore = 0;
    const athleticsStrengths = [];
    const athleticsWeaknesses = [];
    if (sprint < t.sprintElite) { athleticsScore += 30; athleticsStrengths.push('Elite sprinting speed'); }
    else if (sprint < t.sprintGood) { athleticsScore += 20; athleticsStrengths.push('Strong sprinting'); }
    else { athleticsWeaknesses.push('Sprint speed needs improvement'); }
    if (broadJump > t.bJumpHigh) { athleticsScore += 25; athleticsStrengths.push('Excellent explosive power'); }
    else if (broadJump > t.bJumpMid) { athleticsScore += 15; }
    if (verticalJump > t.vJumpElite) { athleticsScore += 20; athleticsStrengths.push('Outstanding vertical jump'); }
    else if (verticalJump > t.vJumpMid) { athleticsScore += 10; }
    athleticsScore += 15; // Base score
    suits.push({ sport: 'Athletics', score: Math.min(athleticsScore, 100), strengths: athleticsStrengths, weaknesses: athleticsWeaknesses });

    // Cycling - requires endurance and leg power
    let cyclingScore = 0;
    const cyclingStrengths = [];
    const cyclingWeaknesses = [];
    if (endurance < t.enduranceElite) { cyclingScore += 30; cyclingStrengths.push('Excellent endurance'); }
    else if (endurance < t.enduranceGood) { cyclingScore += 20; cyclingStrengths.push('Good endurance'); }
    else { cyclingWeaknesses.push('Endurance needs development'); }
    if (broadJump > t.bJumpMid + 30) { cyclingScore += 20; cyclingStrengths.push('Strong leg power'); }
    if (weight < t.weightMax && height > t.heightCycling) { cyclingScore += 15; cyclingStrengths.push('Ideal build for cycling'); }
    cyclingScore += 25; // Base score
    suits.push({ sport: 'Cycling', score: Math.min(cyclingScore, 100), strengths: cyclingStrengths, weaknesses: cyclingWeaknesses });

    // Volleyball - requires height, jumping, and agility
    let volleyballScore = 0;
    const volleyballStrengths = [];
    const volleyballWeaknesses = [];
    if (height > t.heightTall) { volleyballScore += 25; volleyballStrengths.push('Good height advantage'); }
    else if (height > t.heightMid) { volleyballScore += 15; }
    else { volleyballWeaknesses.push('Height could be limiting'); }
    if (verticalJump > t.vJumpElite) { volleyballScore += 30; volleyballStrengths.push('Exceptional jumping'); }
    else if (verticalJump > t.vJumpMid) { volleyballScore += 20; volleyballStrengths.push('Good jumping ability'); }
    else { volleyballWeaknesses.push('Jumping needs improvement'); }
    if (shuttleRun < t.shuttleFast) { volleyballScore += 20; volleyballStrengths.push('Quick movements'); }
    volleyballScore += 15; // Base score
    suits.push({ sport: 'Volleyball', score: Math.min(volleyballScore, 100), strengths: volleyballStrengths, weaknesses: volleyballWeaknesses });
    
    return suits.sort((a, b) => b.score - a.score);
  };

  const getSWOTAnalysis = () => {
    const strengths = [];
    const weaknesses = [];
    const opportunities = [];
    const threats = [];

    const genderLabel = (athlete.gender_normalized || '').toLowerCase() === 'female' ? 'females' : 
                        (athlete.gender_normalized || '').toLowerCase() === 'male' ? 'males' : 'peers';
    
    // Use gender percentiles if available, fall back to overall
    const gp = (metric) => athlete[`${metric}_gender_percentile`] ?? athlete[`${metric}_percentile`];
    
    // Strengths - metrics >= 75th percentile (within gender)
    if (gp('height_cm') >= 75) strengths.push(`Height (${formatNumber(athlete.height_cm)} cm) - Top 25% among ${genderLabel}`);
    if (gp('weight_kg') >= 75) strengths.push(`Weight (${formatNumber(athlete.weight_kg)} kg) - Top 25% among ${genderLabel}`);
    if (gp('vertical_jump_cm') >= 75) strengths.push(`Vertical Jump (${formatNumber(athlete.vertical_jump_cm)} cm) - Excellent explosive power`);
    if (gp('broad_jump_cm') >= 75) strengths.push(`Broad Jump (${formatNumber(athlete.broad_jump_cm)} cm) - Outstanding leg power`);
    if (gp('sprint_30m_sec') >= 75) strengths.push(`30m Sprint (${formatNumber(athlete.sprint_30m_sec)} s) - Elite speed among ${genderLabel}`);
    if (gp('run_800m_sec') >= 75) strengths.push(`800m Run (${formatTime(athlete.run_800m_sec)}) - Superior endurance`);
    if (gp('shuttle_run_sec') >= 75) strengths.push(`Shuttle Run (${formatNumber(athlete.shuttle_run_sec)} s) - Exceptional agility`);
    if (gp('football_throw_m') >= 75) strengths.push(`Football Throw (${formatNumber(athlete.football_throw_m)} m) - Strong upper body`);
    
    // Weaknesses - metrics < 25th percentile (within gender)
    if (gp('height_cm') < 25) weaknesses.push(`Height (${formatNumber(athlete.height_cm)} cm) - Below average among ${genderLabel}`);
    if (gp('weight_kg') < 25) weaknesses.push(`Weight (${formatNumber(athlete.weight_kg)} kg) - May need strength building`);
    if (gp('vertical_jump_cm') < 25) weaknesses.push(`Vertical Jump (${formatNumber(athlete.vertical_jump_cm)} cm) - Needs explosive power training`);
    if (gp('broad_jump_cm') < 25) weaknesses.push(`Broad Jump (${formatNumber(athlete.broad_jump_cm)} cm) - Leg power development needed`);
    if (gp('sprint_30m_sec') < 25) weaknesses.push(`30m Sprint (${formatNumber(athlete.sprint_30m_sec)} s) - Speed training required`);
    if (gp('run_800m_sec') < 25) weaknesses.push(`800m Run (${formatTime(athlete.run_800m_sec)}) - Endurance building needed`);
    if (gp('shuttle_run_sec') < 25) weaknesses.push(`Shuttle Run (${formatNumber(athlete.shuttle_run_sec)} s) - Agility drills recommended`);
    if (gp('football_throw_m') < 25) weaknesses.push(`Football Throw (${formatNumber(athlete.football_throw_m)} m) - Upper body strength needed`);
    
    // Opportunities - based on top sport recommendations
    const topSports = getSportSuitability().slice(0, 3);
    topSports.forEach(sport => {
      if (sport.score >= 60) {
        opportunities.push(`${sport.sport} - ${sport.score}% match, strong potential for competitive training`);
      }
    });
    
    // Add general opportunities
    if (strengths.length >= 3) {
      opportunities.push('Multiple strong metrics indicate high athletic potential');
    }
    const bmi = athlete.bmi || (athlete.weight_kg && athlete.height_cm ? (athlete.weight_kg / Math.pow(athlete.height_cm / 100, 2)).toFixed(1) : null);
    if (bmi >= 18.5 && bmi < 25) {
      opportunities.push('Healthy BMI provides good foundation for athletic development');
    }
    if (athlete.age && athlete.age < 16) {
      opportunities.push('Young age allows for significant skill and physical development');
    }
    
    // Threats - potential limiting factors
    if (weaknesses.length >= 4) {
      threats.push('Multiple areas need improvement - requires comprehensive training program');
    }
    if (bmi < 18.5) {
      threats.push('Underweight - may affect strength and endurance, nutritional support needed');
    }
    if (bmi >= 25) {
      threats.push('Overweight - may limit agility and speed, weight management recommended');
    }
    if (gp('sprint_30m_sec') < 25 && gp('run_800m_sec') < 25) {
      threats.push('Both speed and endurance are weak - cardiovascular fitness is a priority');
    }
    if (gp('vertical_jump_cm') < 25 && gp('broad_jump_cm') < 25) {
      threats.push('Limited explosive power - may restrict sport options without targeted training');
    }
    
    return { strengths, weaknesses, opportunities, threats };
  };

  const sportRecommendations = getSportSuitability();
  const { strengths, weaknesses, opportunities, threats } = getSWOTAnalysis();

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl border border-slate-700 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-700 bg-slate-900/50">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {athlete.full_name || `${athlete.first_name || ''} ${athlete.last_name || ''}`.trim() || 'Athlete Details'}
              </h2>
              <p className="text-sm text-slate-400">
                ID: {athlete.student_id || athlete.sl_no} • {athlete.gender || 'N/A'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="border-b border-slate-700">
          <div className="flex space-x-1 px-6">
            {[
              { id: 'overview', label: 'Overview', icon: Activity },
              { id: 'performance', label: 'Performance', icon: TrendingUp },
              { id: 'insights', label: 'AI Insights', icon: Brain },
              { id: 'sports', label: 'Sports', icon: Award },
              { id: 'health', label: 'Health', icon: Heart },
              { id: 'nutrition', label: 'Nutrition', icon: Utensils }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                    activeTab === tab.id ? 'text-blue-400' : 'text-slate-400 hover:text-slate-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </div>
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6 space-y-6">
          {activeTab === 'overview' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                  <p className="text-xs text-slate-400 mb-1">Height</p>
                  <p className="text-2xl font-bold text-white">{formatNumber(athlete.height_cm)} cm</p>
                  {athlete.height_percentile && (
                    <p className="text-xs text-blue-400 mt-1">{athlete.height_percentile}th percentile</p>
                  )}
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                  <p className="text-xs text-slate-400 mb-1">Weight</p>
                  <p className="text-2xl font-bold text-white">{formatNumber(athlete.weight_kg)} kg</p>
                  {athlete.weight_percentile && (
                    <p className="text-xs text-blue-400 mt-1">{athlete.weight_percentile}th percentile</p>
                  )}
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                  <p className="text-xs text-slate-400 mb-1">BMI</p>
                  <p className="text-2xl font-bold text-white">{formatNumber(bmi)}</p>
                  <p className={`text-xs mt-1 ${
                    bmiCategory === 'Normal' ? 'text-green-400' :
                    bmiCategory === 'Underweight' ? 'text-yellow-400' : 'text-orange-400'
                  }`}>{bmiCategory}</p>
                </div>
              </div>

              <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                  <span>Performance Profile</span>
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="#475569" />
                      <PolarAngleAxis dataKey="metric" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#64748b' }} />
                      <Radar name="Athlete" dataKey="value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-3">Additional Information</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {athlete.student_id && (
                    <div><span className="text-slate-400">Student ID:</span><span className="text-white ml-2">{athlete.student_id}</span></div>
                  )}
                  {athlete.school_name && (
                    <div><span className="text-slate-400">School:</span><span className="text-white ml-2">{athlete.school_name}</span></div>
                  )}
                  {athlete.district_name && (
                    <div><span className="text-slate-400">District:</span><span className="text-white ml-2">{athlete.district_name}</span></div>
                  )}
                  {athlete.date_of_birth && (
                    <div><span className="text-slate-400">Date of Birth:</span><span className="text-white ml-2">{new Date(athlete.date_of_birth).toLocaleDateString()}</span></div>
                  )}
                </div>
              </div>
            </>
          )}

          {activeTab === 'performance' && (
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">Performance Metrics</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {athlete.vertical_jump_cm && (
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">Vertical Jump</p>
                    <p className="text-lg font-bold text-white">{formatNumber(athlete.vertical_jump_cm)} cm</p>
                    {athlete.vertical_jump_percentile && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-slate-400">Percentile</span>
                          <span className={`font-semibold ${
                            athlete.vertical_jump_percentile >= 75 ? 'text-green-400' :
                            athlete.vertical_jump_percentile >= 50 ? 'text-yellow-400' :
                            'text-orange-400'
                          }`}>
                            {athlete.vertical_jump_percentile}th
                          </span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${
                              athlete.vertical_jump_percentile >= 75 ? 'bg-green-400' :
                              athlete.vertical_jump_percentile >= 50 ? 'bg-yellow-400' :
                              'bg-orange-400'
                            }`}
                            style={{ width: `${athlete.vertical_jump_percentile}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {athlete.broad_jump_cm && (
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">Broad Jump</p>
                    <p className="text-lg font-bold text-white">{formatNumber(athlete.broad_jump_cm)} cm</p>
                    {athlete.broad_jump_percentile && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-slate-400">Percentile</span>
                          <span className={`font-semibold ${
                            athlete.broad_jump_percentile >= 75 ? 'text-green-400' :
                            athlete.broad_jump_percentile >= 50 ? 'text-yellow-400' :
                            'text-orange-400'
                          }`}>
                            {athlete.broad_jump_percentile}th
                          </span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${
                              athlete.broad_jump_percentile >= 75 ? 'bg-green-400' :
                              athlete.broad_jump_percentile >= 50 ? 'bg-yellow-400' :
                              'bg-orange-400'
                            }`}
                            style={{ width: `${athlete.broad_jump_percentile}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {athlete.sprint_30m_sec && (
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">30m Sprint</p>
                    <p className="text-lg font-bold text-white">{formatNumber(athlete.sprint_30m_sec)} s</p>
                    {athlete.sprint_30m_percentile && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-slate-400">Percentile</span>
                          <span className={`font-semibold ${
                            athlete.sprint_30m_percentile >= 75 ? 'text-green-400' :
                            athlete.sprint_30m_percentile >= 50 ? 'text-yellow-400' :
                            'text-orange-400'
                          }`}>
                            {athlete.sprint_30m_percentile}th
                          </span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${
                              athlete.sprint_30m_percentile >= 75 ? 'bg-green-400' :
                              athlete.sprint_30m_percentile >= 50 ? 'bg-yellow-400' :
                              'bg-orange-400'
                            }`}
                            style={{ width: `${athlete.sprint_30m_percentile}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {athlete.shuttle_run_sec && (
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">Shuttle Run</p>
                    <p className="text-lg font-bold text-white">{formatNumber(athlete.shuttle_run_sec)} s</p>
                    {athlete.shuttle_run_percentile && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-slate-400">Percentile</span>
                          <span className={`font-semibold ${
                            athlete.shuttle_run_percentile >= 75 ? 'text-green-400' :
                            athlete.shuttle_run_percentile >= 50 ? 'text-yellow-400' :
                            'text-orange-400'
                          }`}>
                            {athlete.shuttle_run_percentile}th
                          </span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${
                              athlete.shuttle_run_percentile >= 75 ? 'bg-green-400' :
                              athlete.shuttle_run_percentile >= 50 ? 'bg-yellow-400' :
                              'bg-orange-400'
                            }`}
                            style={{ width: `${athlete.shuttle_run_percentile}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {athlete.football_throw_m && (
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">Football Throw</p>
                    <p className="text-lg font-bold text-white">{formatNumber(athlete.football_throw_m)} m</p>
                    {athlete.football_throw_percentile && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-slate-400">Percentile</span>
                          <span className={`font-semibold ${
                            athlete.football_throw_percentile >= 75 ? 'text-green-400' :
                            athlete.football_throw_percentile >= 50 ? 'text-yellow-400' :
                            'text-orange-400'
                          }`}>
                            {athlete.football_throw_percentile}th
                          </span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${
                              athlete.football_throw_percentile >= 75 ? 'bg-green-400' :
                              athlete.football_throw_percentile >= 50 ? 'bg-yellow-400' :
                              'bg-orange-400'
                            }`}
                            style={{ width: `${athlete.football_throw_percentile}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {athlete.run_800m_sec && (
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">800m Run</p>
                    <p className="text-lg font-bold text-white">{formatTime(athlete.run_800m_sec)}</p>
                    {athlete.run_800m_percentile && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-slate-400">Percentile</span>
                          <span className={`font-semibold ${
                            athlete.run_800m_percentile >= 75 ? 'text-green-400' :
                            athlete.run_800m_percentile >= 50 ? 'text-yellow-400' :
                            'text-orange-400'
                          }`}>
                            {athlete.run_800m_percentile}th
                          </span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${
                              athlete.run_800m_percentile >= 75 ? 'bg-green-400' :
                              athlete.run_800m_percentile >= 50 ? 'bg-yellow-400' :
                              'bg-orange-400'
                            }`}
                            style={{ width: `${athlete.run_800m_percentile}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'insights' && (
            <>
              <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/30 p-6 mb-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                  <Brain className="w-6 h-6 text-blue-400" />
                  <span>SWOT Analysis</span>
                </h3>
                <p className="text-slate-300 text-sm mb-6">Comprehensive performance analysis based on percentile rankings and physical metrics</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Strengths */}
                  <div className="bg-green-900/20 border border-green-600/30 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-green-400 mb-3 flex items-center space-x-2">
                      <Zap className="w-5 h-5" />
                      <span>Strengths</span>
                    </h4>
                    {strengths.length > 0 ? (
                      <ul className="space-y-2 text-sm text-slate-300">
                        {strengths.map((item, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <span className="text-green-400 mt-0.5">✓</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-slate-400 text-sm">No metrics in top 25% yet - opportunity for growth!</p>
                    )}
                  </div>

                  {/* Weaknesses */}
                  <div className="bg-orange-900/20 border border-orange-600/30 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-orange-400 mb-3 flex items-center space-x-2">
                      <Activity className="w-5 h-5" />
                      <span>Weaknesses</span>
                    </h4>
                    {weaknesses.length > 0 ? (
                      <ul className="space-y-2 text-sm text-slate-300">
                        {weaknesses.map((item, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <span className="text-orange-400 mt-0.5">⚠</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-slate-400 text-sm">No significant weaknesses identified - well-rounded athlete!</p>
                    )}
                  </div>

                  {/* Opportunities */}
                  <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-blue-400 mb-3 flex items-center space-x-2">
                      <TrendingUp className="w-5 h-5" />
                      <span>Opportunities</span>
                    </h4>
                    {opportunities.length > 0 ? (
                      <ul className="space-y-2 text-sm text-slate-300">
                        {opportunities.map((item, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <span className="text-blue-400 mt-0.5">→</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-slate-400 text-sm">Focus on building strengths to unlock opportunities</p>
                    )}
                  </div>

                  {/* Threats */}
                  <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-red-400 mb-3 flex items-center space-x-2">
                      <Award className="w-5 h-5" />
                      <span>Threats/Challenges</span>
                    </h4>
                    {threats.length > 0 ? (
                      <ul className="space-y-2 text-sm text-slate-300">
                        {threats.map((item, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <span className="text-red-400 mt-0.5">!</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-slate-400 text-sm">No major threats identified - continue current training path</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'sports' && (
            <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Award className="w-5 h-5 text-yellow-400" />
                <span>Recommended Sports</span>
              </h3>
              <div className="space-y-4">
                {sportRecommendations.map((rec, idx) => (
                  <div key={idx} className="bg-slate-800/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-semibold text-white text-lg">{rec.sport}</span>
                      <span className="text-lg font-bold text-blue-400">{rec.score}% Match</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2.5">
                      <div className="bg-gradient-to-r from-blue-500 to-purple-500 h-2.5 rounded-full" style={{ width: `${rec.score}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'health' && (
            <>
              <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                  <Heart className="w-5 h-5 text-red-400" />
                  <span>Health Metrics</span>
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">BMI</p>
                    <p className="text-2xl font-bold text-white">{formatNumber(bmi)}</p>
                    <p className={`text-sm mt-1 ${
                      bmiCategory === 'Normal' ? 'text-green-400' :
                      bmiCategory === 'Underweight' ? 'text-yellow-400' : 'text-orange-400'
                    }`}>{bmiCategory}</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">BMI Category</p>
                    <p className="text-sm text-slate-300 mt-2">
                      {bmiCategory === 'Underweight' && 'May need increased caloric intake'}
                      {bmiCategory === 'Normal' && 'Healthy weight range'}
                      {bmiCategory === 'Overweight' && 'Consider balanced diet and exercise'}
                      {bmiCategory === 'Obese' && 'Consult healthcare professional'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-900/20 border border-blue-600 rounded-lg p-4">
                <h4 className="text-white font-semibold mb-2">Health Recommendations</h4>
                <ul className="text-sm text-slate-300 space-y-2">
                  <li>• Regular physical activity: 60 minutes daily</li>
                  <li>• Balanced diet with adequate protein, carbs, and healthy fats</li>
                  <li>• Stay hydrated: 2-3 liters of water daily</li>
                  <li>• Adequate sleep: 8-10 hours for growing athletes</li>
                  <li>• Regular health check-ups</li>
                </ul>
              </div>
            </>
          )}

          {activeTab === 'nutrition' && (
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Utensils className="w-5 h-5 text-green-400" />
                <span>Bihar-Specific Nutrition Plan</span>
              </h3>
              
              <div className="space-y-4">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-3">Daily Meal Plan</h4>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="text-blue-400 font-medium">Breakfast (7:00 AM)</p>
                      <p className="text-slate-300">Sattu drink + Poha/Upma + Banana + Milk</p>
                    </div>
                    <div>
                      <p className="text-blue-400 font-medium">Mid-Morning Snack (10:00 AM)</p>
                      <p className="text-slate-300">Seasonal fruit + Handful of peanuts/chana</p>
                    </div>
                    <div>
                      <p className="text-blue-400 font-medium">Lunch (1:00 PM)</p>
                      <p className="text-slate-300">Dal + Rice + Seasonal vegetables + Roti + Curd</p>
                    </div>
                    <div>
                      <p className="text-blue-400 font-medium">Evening Snack (4:00 PM)</p>
                      <p className="text-slate-300">Litti Chokha / Sattu paratha + Jaggery</p>
                    </div>
                    <div>
                      <p className="text-blue-400 font-medium">Dinner (8:00 PM)</p>
                      <p className="text-slate-300">Roti + Sabzi + Dal + Milk</p>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-3">Traditional Bihar Foods for Athletes</h4>
                  <ul className="text-sm text-slate-300 space-y-2">
                    <li>• <span className="text-yellow-400 font-medium">Sattu:</span> Excellent protein source, mix with water/milk</li>
                    <li>• <span className="text-yellow-400 font-medium">Litti Chokha:</span> Complex carbs and proteins</li>
                    <li>• <span className="text-yellow-400 font-medium">Dal-Chawal:</span> Complete protein when combined</li>
                    <li>• <span className="text-yellow-400 font-medium">Seasonal Fruits:</span> Mango, Litchi, Banana for energy</li>
                    <li>• <span className="text-yellow-400 font-medium">Milk & Curd:</span> Calcium and protein for bones</li>
                  </ul>
                </div>

                {bmiCategory === 'Underweight' && (
                  <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-4">
                    <h4 className="text-yellow-400 font-semibold mb-2">Special Recommendations for Weight Gain</h4>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• Increase caloric intake by 300-500 calories per day</li>
                      <li>• Add ghee to dal and roti</li>
                      <li>• Sattu with milk and banana (high calorie)</li>
                      <li>• Nuts and dry fruits between meals</li>
                      <li>• 5-6 small meals throughout the day</li>
                    </ul>
                  </div>
                )}

                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-3">Hydration</h4>
                  <p className="text-sm text-slate-300">
                    Drink 3-4 liters of water daily, especially important in Bihar's hot climate. 
                    Increase intake during training sessions.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
